import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { STYLES, MULLIGAN_TIPS, OPPOSITE } from "@/data/quiz";
import { API, trackEvent } from "@/lib/api";

const EASE = [0.22, 1, 0.36, 1];

export default function Result() {
  const { id } = useParams();
  const [result, setResult] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/quiz/results/${id}`)
      .then(({ data }) => {
        setResult(data);
        const styleName = STYLES[data.archetype]?.name;
        document.title = `${styleName ? styleName + " Profile" : "Wired2Win Result"} — Wired 2 Win Golf`;
        trackEvent("shared_result_view");
      })
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound)
    return (
      <main data-testid="result-not-found" className="pt-48 pb-32 max-w-3xl mx-auto px-6 text-center">
        <h1 className="font-display font-black text-5xl uppercase tracking-tighter">Lost ball.</h1>
        <p className="text-white/50 mt-4">That result link doesn't exist (anymore).</p>
        <Link to="/quiz" data-testid="result-not-found-quiz-cta" className="inline-block mt-8 text-volt font-bold text-sm hover:text-white transition-colors">
          Take the assessment yourself
        </Link>
      </main>
    );

  if (!result) return <main className="pt-48 pb-32 max-w-3xl mx-auto px-6 text-white/40 text-sm">Loading...</main>;

  const primary = STYLES[result.archetype];
  const accent = STYLES[result.accent];
  const mulligan = result.growth_zone ? STYLES[result.growth_zone] : null;
  if (!primary)
    return <main className="pt-48 pb-32 max-w-3xl mx-auto px-6 text-white/40 text-sm">This result predates the current profile format.</main>;

  return (
    <main data-testid="shared-result-page" className="pt-32 lg:pt-40">
      <div className="max-w-3xl mx-auto px-6 pb-28">
        <Reveal>
          <p className="text-xs tracking-[0.25em] uppercase font-bold text-volt mb-4">
            {result.name ? `${result.name}'s` : "A golfer's"} Wired2Win Profile
          </p>
          <h1 className="font-display font-black tracking-tighter leading-[0.95] text-5xl sm:text-7xl uppercase">
            {primary.name}
          </h1>
          <p className="mt-3 text-white/60 text-lg">{primary.tagline}</p>
          {accent && (
            <p className="mt-2 text-white/50 text-sm">
              Accent: <span className="text-white font-semibold">{accent.name}</span>
              {result.blend && <span className="text-volt"> — a strong blend</span>}
              {result.adaptive && <span className="text-volt"> — situational/adaptive profile</span>}
            </p>
          )}
        </Reveal>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-6">
          {Object.entries(STYLES).map(([key, style]) => (
            <div key={key} data-testid={`shared-score-${key}`}>
              <div className="flex justify-between text-xs font-bold uppercase tracking-widest mb-2">
                <span className="text-white/70">{style.name}</span>
                <span className="text-volt">{result.scores?.[key] ?? "—"}</span>
              </div>
              <div className="h-1.5 bg-white/10">
                <motion.div
                  className="h-full bg-volt"
                  initial={{ width: 0 }}
                  animate={{ width: `${result.scores?.[key] || 0}%` }}
                  transition={{ duration: 1, delay: 0.3, ease: EASE }}
                />
              </div>
            </div>
          ))}
        </div>

        <Reveal delay={0.2}>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="border border-white/10 bg-ink p-8">
              <p className="text-xs tracking-[0.25em] uppercase font-bold text-white/40 mb-3">How they're wired</p>
              <p className="text-white/70 text-sm leading-relaxed">{primary.strength}</p>
              <p className="text-white/50 text-sm leading-relaxed mt-4">{primary.shadow}</p>
            </div>
            {mulligan && (
              <div className="border border-volt/40 bg-volt/5 p-8">
                <p className="text-xs tracking-[0.25em] uppercase font-bold text-volt mb-3">
                  Mental Mulligan — borrow from {mulligan.name}
                </p>
                <p className="text-white/80 text-sm leading-relaxed">{MULLIGAN_TIPS[OPPOSITE[result.growth_zone]]}</p>
              </div>
            )}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-12 border border-white/10 bg-ink p-8 flex flex-col sm:flex-row sm:items-center gap-6 justify-between">
            <p className="font-display font-bold text-2xl tracking-tight max-w-sm">
              How are <span className="text-volt">you</span> wired?
            </p>
            <Link
              to="/quiz"
              data-testid="shared-result-quiz-cta"
              onClick={() => trackEvent("cta_click", { location: "shared-result" })}
              className="group inline-flex items-center gap-2 bg-volt text-obsidian font-display font-bold px-6 py-3.5 hover:bg-white transition-colors shrink-0"
            >
              Take the Free Assessment <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </Link>
          </div>
        </Reveal>
      </div>
    </main>
  );
}
